// GL16 GPC 배송비 분담금 단가 관리 — MASS 호출부.
import request, { getExcel } from '@/utils/request'
import { Utils } from '@/utils/Utils'
import { parseExcelFileName } from './mapping'
import type {
  GpcRateExcelUploadRes,
  GpcRateExcelValidation,
  GpcRateHistoryReq,
  GpcRateHistoryRes,
  GpcRateMatrixFilter,
  GpcRateMatrixReq,
  GpcRateMatrixRes,
  GpcRateSaveReq,
  GpcRateSaveRes,
} from './types'

const BASE = '/api2/mass/v1/cost-share/prices'

/**
 * 매트릭스 조회 — 행 단위 페이지(page 0부터, size 최대 1000).
 * 다중 파라미터(countryGroups·logisticsCategoryCodes)는 request.get 이 `Utils.qsStringify`(arrayFormat comma)로
 * `countryGroups=JP,CN` 으로 직렬화하고, BE 는 반복·쉼표 둘 다 받는다.
 */
export const getGpcRateMatrix = (params: GpcRateMatrixReq) =>
  request.get<ApiResponseV2<GpcRateMatrixRes>, GpcRateMatrixReq>(`${BASE}/matrix`, params)

/**
 * 셀 단건 저장 — 201. 400 은 검증 실패(meta.message 가 사용자 문구), 409 는 동시 편집 충돌(meta.errorCode CONFLICT).
 * 항상 새 행을 추가하고, 새 시작일에 걸치는 기존 행은 시작일 전날로 마감한다.
 */
export const saveGpcRatePrice = (body: GpcRateSaveReq) =>
  request.post<ApiResponseV2<GpcRateSaveRes>, GpcRateSaveReq>(BASE, body)

/** 양식 다운로드 URL — 기준일 단가가 채워진 업로드 양식(필터 없음, 국가 그룹 15열). */
export const gpcRateExcelTemplateUrl = (basisDate: string) => `${BASE}/excel-template${Utils.qsStringify({ basisDate })}`

/** 조회 결과 다운로드 URL — 매트릭스와 같은 조건(기준일·국가 그룹·물류카테고리)으로 페이지 없이 전 행. 행 선택과 무관하다. */
export const gpcRateExcelSearchResultUrl = (filter: GpcRateMatrixFilter) =>
  `${BASE}/excel-download${Utils.qsStringify<GpcRateMatrixFilter>(filter)}`

/**
 * xlsx 다운로드 — Content-Disposition 파일명으로 저장한다(없으면 fallbackFileName).
 * responseType blob 이라 400 의 JSON 본문도 Blob 으로 온다 — JSON 으로 되살려 호출부가 meta.message 를 읽게 한다(GL11 선례).
 */
export const downloadGpcRateExcel = async (url: string, fallbackFileName: string): Promise<string> => {
  let res
  try {
    res = await getExcel(url)
  } catch (e) {
    const resp = (e as { response?: { data?: unknown } })?.response
    if (resp?.data instanceof Blob) {
      try {
        resp.data = JSON.parse(await resp.data.text())
      } catch {
        /* JSON 이 아니면 호출부의 기본 문구 */
      }
    }
    throw e
  }
  const fileName = parseExcelFileName(String(res.headers?.['content-disposition'] ?? ''), fallbackFileName)
  const a = document.createElement('a')
  a.href = URL.createObjectURL(res.data)
  a.download = fileName
  a.click()
  URL.revokeObjectURL(a.href)
  return fileName
}

const toFileForm = (file: Blob) => {
  const formData = new FormData()
  formData.append('file', file)
  return formData
}
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } }

/**
 * 엑셀 업로드 검증 미리보기 — 파일만 보낸다(적용 기간은 엑셀 행의 시작일·종료일). 바뀔 셀·오류를 가르고, 서버는 결과를 보관하지 않는다.
 * 파일 단위 문제는 400(meta.errorCode INVALID_EXCEL_FILE · EXCEL_ROW_COUNT_LIMIT_EXCEEDED, meta.message 가 사용자 문구).
 */
export const validateGpcRateExcel = (file: Blob) =>
  request.post<ApiResponseV2<GpcRateExcelValidation>, FormData>(`${BASE}/excel-validation`, toFileForm(file), MULTIPART)

/**
 * 엑셀 확정 저장 — 같은 파일을 다시 보내 저장 시점 단가로 재검증한 뒤 바뀐 셀을 행마다 적힌 시작일부터 적용한다(201).
 * 오류가 있으면 409(meta.errorCode EXCEL_PARTIAL/TOTAL_VALIDATION_FAILURE, data = 그 시점 검증 결과), 동시 저장 충돌은 409 CONFLICT.
 */
export const uploadGpcRateExcel = (file: Blob) =>
  request.post<ApiResponseV2<GpcRateExcelUploadRes>, FormData>(`${BASE}/excel-upload`, toFileForm(file), MULTIPART)

/** 변경 이력 — 최신순, page 0부터·size 최대 100. 다중 필터는 쉼표 직렬화(BE List 바인딩이 쉼표를 나눈다). */
export const getGpcRateHistories = (params: GpcRateHistoryReq) =>
  request.get<ApiResponseV2<GpcRateHistoryRes>, GpcRateHistoryReq>(`${BASE}/histories`, params)
